import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import biApi from "../../../../api/biApi";
import type {
  InvestorPortalFeature,
  InvestorPortalSettings,
  InvestorPortalSettingsResponse,
} from "../../../../api/types/investorPortalTypes";
import { ReportType } from "../../../../api/types/svTypes";
import useClientContext from "../../../../context/useClientContext";
import ScrollableColumn from "../../../common/layout/ScrollableColumn";
import ScrollableColumns from "../../../common/layout/ScrollableColumns";
import StretchedScrollableContent from "../../../common/layout/StretchedScrollableContent";
import { getPageStatusFromPortalSettings } from "../pages/portalPageStatusHelper";
import PortalWidgetDetailsSection from "./PortalWidgetDetailsSection";
import { getRelevantWidgetsForPage } from "./portalWidgetsHelper";
import PortalWidgetsPreviewSection from "./PortalWidgetsPreviewSection";

interface Props {
  page: InvestorPortalFeature;
  portalSettings: InvestorPortalSettings | undefined;
  onPortalSettingsUpdated: (updated: InvestorPortalSettingsResponse) => void;
}

const PortalWidgetSettingsPageContent = ({ page, portalSettings, onPortalSettingsUpdated }: Props) => {
  const { hasAnyPermission, hasPermissions, isPortalFeatureEnabled } = useClientContext();

  const [selectedWidgetId, setSelectedWidgetId] = useState<string>();

  const [
    widgetSettings,
    fetchWidgetSettingsError,
    { setData: setWidgetSettings, fetch: fetchWidgetSettings, isFetching: isFetchingWidgetSettings },
  ] = useFetch(adminApi.getPortalWidgetSettings);

  const hasReportsViewPermissions = hasAnyPermission(["ViewReports", "ManageReports"]);
  const hasReportsEditPermissions = hasPermissions(["ManageReports"]);

  const getSystemReports = useCallback(() => biApi.systemReports.getReports({ reportType: ReportType.Tabular }), []);

  const [systemReports, fetchSystemReportsError, { isFetching: isFetchingSystemReports }] = useFetch(getSystemReports, {
    disabled: !hasReportsViewPermissions,
  });

  const effectiveSystemReports = systemReports ?? [];

  const fetchError = fetchWidgetSettingsError ?? fetchSystemReportsError;
  if (fetchError) {
    logError(fetchError, "[PortalWidgetSettingsPageContent] fetch");
    return <DataLoadingFailed title="Loading portal widget settings failed" />;
  }

  if (
    widgetSettings === undefined ||
    isFetchingWidgetSettings ||
    isFetchingSystemReports ||
    portalSettings === undefined
  ) {
    return <InlineLoader />;
  }

  const handlePortalSettingsUpdated = (updatedSettings: InvestorPortalSettingsResponse) => {
    onPortalSettingsUpdated(updatedSettings);
    fetchWidgetSettings();
  };

  const pageStatus = getPageStatusFromPortalSettings(page, portalSettings);

  const widgets = getRelevantWidgetsForPage(
    page,
    widgetSettings.widgets,
    isPortalFeatureEnabled("InvestorPortalNewsEnabled")
  );

  return (
    <StretchedScrollableContent>
      <ScrollableColumns gridProps={{ columnSpacing: 2.5 }}>
        <ScrollableColumn>
          <PortalWidgetsPreviewSection
            widgets={widgets}
            selectedWidgetId={selectedWidgetId}
            onWidgetSelect={setSelectedWidgetId}
            systemReports={effectiveSystemReports}
            hasReportsViewPermissions={hasReportsViewPermissions}
            pageStatus={pageStatus}
          />
        </ScrollableColumn>
        <ScrollableColumn width="30rem">
          <PortalWidgetDetailsSection
            page={page}
            pageStatus={pageStatus}
            portalSettings={portalSettings}
            selectedWidget={widgets.find((w) => w.id === selectedWidgetId)}
            systemReports={effectiveSystemReports}
            hasReportsViewPermissions={hasReportsViewPermissions}
            hasReportsEditPermissions={hasReportsEditPermissions}
            onWidgetUpdated={setWidgetSettings}
            onPortalSettingsUpdated={handlePortalSettingsUpdated}
            onDeselectWidget={() => setSelectedWidgetId(undefined)}
          />
        </ScrollableColumn>
      </ScrollableColumns>
    </StretchedScrollableContent>
  );
};

export default PortalWidgetSettingsPageContent;
